import React, { useEffect, useState } from 'react';
import { bedrockUsageService, ToolServerSummaryRow } from '../../services/bedrockUsageService';
import '../../styles/BedrockUsage.css';

function relativeTime(isoString: string): string {
  const now = new Date();
  const date = new Date(isoString);
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays <= 30) return `${diffDays} days ago`;
  return date.toLocaleDateString();
}

interface Props {
  startDate: string;
  endDate: string;
  onSelectToolServer: (id: number, name: string) => void;
}

const ToolServerUsageTable: React.FC<Props> = ({ startDate, endDate, onSelectToolServer }) => {
  const [rows, setRows] = useState<ToolServerSummaryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    bedrockUsageService
      .getToolServerSummary(startDate || undefined, endDate || undefined)
      .then((data) => {
        if (!cancelled) {
          setRows(data);
          setLoading(false);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  if (loading) {
    return (
      <div className="usage-table-container">
        <div className="usage-loading">Loading tool server usage…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="usage-table-container">
        <div className="usage-empty" style={{ color: 'var(--error-color, #f44336)' }}>
          Error: {error}
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="usage-table-container">
        <div className="usage-empty">No tool server usage data found for the selected period.</div>
      </div>
    );
  }

  return (
    <div className="usage-table-container">
      <table className="usage-table">
        <thead>
          <tr>
            <th>Tool Server</th>
            <th>Total Calls</th>
            <th>Input Tokens</th>
            <th>Output Tokens</th>
            <th>Total Tokens</th>
            <th>Orgs Using</th>
            <th>Users Using</th>
            <th>Last Active</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.tool_server_id}>
              <td>
                <span
                  className="usage-clickable-cell"
                  onClick={() => onSelectToolServer(row.tool_server_id, row.tool_server_name)}
                >
                  {row.tool_server_name}
                </span>
              </td>
              <td>{row.total_calls.toLocaleString()}</td>
              <td>{row.sum_input_tokens.toLocaleString()}</td>
              <td>{row.sum_output_tokens.toLocaleString()}</td>
              <td>{row.sum_actual_tokens.toLocaleString()}</td>
              <td>{row.orgs_count.toLocaleString()}</td>
              <td>{row.users_count.toLocaleString()}</td>
              <td>{relativeTime(row.last_call)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ToolServerUsageTable;
